import { useRef, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckboxStateValues } from "@ucl/react-library";
import { useTranslation } from "react-i18next";
import { FormSections, useCaseFormSetup } from "src/pages/case-landing/case-form-setup";

type UseMultiStepFormOptions = {
  initialSections: FormSections[];
  totalSteps?: number;
};

export const useMultiStepForm = ({
  initialSections,
  totalSteps = 2,
}: UseMultiStepFormOptions) => {
  const { t } = useTranslation();
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [showError, setShowError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialSectionsRef = useRef(initialSections);

  const formSections = useMemo<FormSections[]>(() => {
    const sections: FormSections[] = [...initialSectionsRef.current];
    if (showEditDetails) {
      sections.push("contactDetails", "preferredContactChannel");
    }
    return sections;
  }, [showEditDetails]);

  const methods = useCaseFormSetup({ formSections, t });

  const checked = methods.watch("agreeTsAndCs");
  const isAgreed = checked === CheckboxStateValues.CHECKED;

  const currentStepParam = Number(searchParams.get("step") ?? 1);
  const currentStep =
    currentStepParam >= 1 && currentStepParam <= totalSteps ? currentStepParam : 1;

  const goToStep = (step: number) => {
    setSearchParams({ step: String(step) });
  };

  const handleContinue = async () => {
    const isValid = await methods.trigger(["email", "homePhone", "address", "preferredContactChannel"]);
    if (!isValid) return;
    goToStep(currentStep + 1);
  };

  const handleBack = () => {
    setShowError(false);
    if (currentStep > 1) {
      goToStep(currentStep - 1);
      return;
    }
    navigate("/case");
  };

  return {
    t,
    showEditDetails,
    setShowEditDetails,
    showError,
    setShowError,
    methods,
    isAgreed,
    currentStep,
    totalSteps,
    handleContinue,
    handleBack,
  };
};
